import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authStatus, authLogin, authLoginVerify, lostTwoFA, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, LogIn, ArrowLeft } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lostMsg, setLostMsg] = useState("");
  const [lostLoading, setLostLoading] = useState(false);

  const requestLost = async () => {
    setError("");
    setLostLoading(true);
    try {
      const res = await lostTwoFA(mfaToken);
      setLostMsg(`Reset link sent to ${res.email}. It expires in 1 hour.`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLostLoading(false);
    }
  };

  useEffect(() => {
    authStatus().then((s) => {
      if (s.needs_setup) navigate("/setup", { replace: true });
    }).catch(() => {});
  }, [navigate]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await authLogin({ email, password });
      if (res.requires_2fa) setMfaToken(res.mfa_token);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await authLoginVerify({ mfa_token: mfaToken, code });
      login(res.token, res.user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (mfaToken) {
    return (
      <AuthShell title="Two-factor verification" subtitle="Enter the 6-digit code from your authenticator app.">
        <form onSubmit={submitCode} className="space-y-5">
          <div>
            <Label className="label-caps">Authentication code</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              autoFocus
              className="mt-1.5 text-center text-lg tracking-[0.5em] font-mono"
              data-testid="login-2fa-input"
            />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading || code.length !== 6} className="w-full gap-2 text-white" style={{ background: "#002FA7" }} data-testid="login-2fa-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Verify & Sign In
          </Button>
          <button type="button" onClick={() => { setMfaToken(null); setCode(""); setError(""); setLostMsg(""); }} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mx-auto" data-testid="login-back">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="border-t border-slate-200 pt-4 text-center">
            {lostMsg ? (
              <p className="text-sm text-emerald-700" data-testid="login-lost-2fa-sent">{lostMsg}</p>
            ) : (
              <button type="button" onClick={requestLost} disabled={lostLoading} className="text-xs text-[#002FA7] hover:underline" data-testid="login-lost-2fa">
                {lostLoading ? "Sending…" : "Lost your authenticator? Email me a reset link"}
              </button>
            )}
          </div>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Sign in" subtitle="Access the Soverenx document generator.">
      <form onSubmit={submitPassword} className="space-y-5">
        <div>
          <Label className="label-caps">Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@soverenxsystems.com.au" autoFocus className="mt-1.5" data-testid="login-email" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="label-caps">Password</Label>
            <Link to="/forgot-password" className="text-xs text-[#002FA7] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
          </div>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" data-testid="login-password" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading || !email || !password} className="w-full gap-2 text-white" style={{ background: "#002FA7" }} data-testid="login-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Continue
        </Button>
      </form>
    </AuthShell>
  );
};

export default Login;
